import { Instagram, ExternalLink } from "lucide-react";
import { useSettings } from "../../../auth/SettingsContext";
import { SectionHeader } from "./EditorialDepartments";

/**
 * Premium Instagram showcase. Pulls posts directly from
 * `useSettings().instagram_posts` — the same admin-configured source the
 * footer uses. Masonry feel via an uneven 6-tile grid (2 large + 4 small).
 * Falls back gracefully when no posts are configured.
 */
export default function InstagramShowcase() {
    const s = useSettings();
    const handle = s.instagram_handle || "jns.cration.ds";
    const url = s.instagram_url || `https://www.instagram.com/${handle}`;
    const posts = Array.isArray(s.instagram_posts) ? s.instagram_posts : [];

    // Don't render the section at all if there's nothing to show — keeps
    // the homepage clean for fresh installs.
    if (!posts.length) return null;

    return (
        <section
            data-testid="shop-instagram"
            className="mb-24 lg:mb-32 animate-fade-up"
        >
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
                <SectionHeader
                    overline="Follow our journey"
                    title={<>@<span className="italic text-wine">{handle}</span></>}
                    subtitle="Behind the scenes from the studio, new arrivals, styling tips and customer love."
                />
                <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="shop-instagram-cta"
                    className="inline-flex items-center gap-2 h-11 px-5 rounded-full
                               border border-ink/15 text-ink text-[11px] font-bold uppercase tracking-[0.18em]
                               hover:border-rosegold hover:text-wine transition-colors flex-shrink-0"
                >
                    <Instagram size={14} /> Follow <ExternalLink size={11} />
                </a>
            </div>

            {/* Masonry-style grid: 2 large + 4 small */}
            <div
                data-testid="shop-instagram-grid"
                className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 lg:gap-4"
            >
                {posts.slice(0, 6).map((p, i) => {
                    const isLarge = i === 0 || i === 3; // staggered emphasis
                    return (
                        <a
                            key={i}
                            href={p.url || url}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`shop-ig-post-${i}`}
                            className={`group relative block overflow-hidden rounded-2xl bg-ink/10
                                        ${isLarge ? "aspect-square lg:row-span-2 lg:aspect-auto" : "aspect-square"}
                                        ${i >= 4 ? "hidden sm:block" : ""}`}
                        >
                            <img
                                src={p.image}
                                alt={`Instagram post ${i + 1}`}
                                className="absolute inset-0 w-full h-full object-cover
                                           transition-transform duration-[1200ms] ease-out group-hover:scale-[1.06]"
                                loading="lazy"
                                onError={(e) => { e.target.src = "/default-item.svg"; }}
                            />
                            {/* Hover overlay */}
                            <div className="absolute inset-0 bg-ink/0 group-hover:bg-ink/55 transition-colors flex items-center justify-center">
                                <div className="inline-flex items-center gap-2 px-3 h-9 rounded-full bg-ivory/95 text-ink
                                                text-[10px] font-bold uppercase tracking-[0.22em]
                                                opacity-0 group-hover:opacity-100 transition-opacity">
                                    <Instagram size={12} /> View
                                </div>
                            </div>
                        </a>
                    );
                })}
            </div>
        </section>
    );
}
